import axios, {
	AxiosInstance,
	InternalAxiosRequestConfig,
	AxiosResponse,
	AxiosRequestConfig,
	AxiosError,
} from "axios";
import { toast } from "react-toastify";

// Override locally with REACT_APP_API_URL in .env.development.local
// (CRA reads REACT_APP_* vars at build time).
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.styola.com/api/v1";

// Send the browser's timezone on every request — a few backend endpoints
// (e.g. /orders/merchant/dashboard-summary) bucket aggregates by the caller's
// local day. Harmless for every other endpoint.
const browserTimezone =
	(typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) ||
	"Asia/Kolkata";

// Create a custom axios instance with enhanced logging
const apiClient: AxiosInstance = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		"Content-Type": "application/json",
		timezone: browserTimezone,
	},
	timeout: 30000, // 30 second timeout
});

// Log all requests for debugging
apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		const token =
			localStorage.getItem("token") ||
			"eyJhbGciOiJIUzI1NiJ9.eyJyb2xlIjoiUk9MRV9NRVJDSEFOVCIsImlkIjoiNjdiZTA0YTAzZGYyZGExMDhkYjQ3ODNmIiwic3ViIjoiaGl0ZXNoZ2FyZzAyQGdtYWlsLmNvbSIsImlhdCI6MTc0NDEzMjQxMywiZXhwIjoxNzQ0NzM3MjEzfQ.RhQqlUT10QbG-LMVkLL9H7hOAoxDq_7P07N5t9c-5kU";
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}

		// If the data is FormData, remove the Content-Type header to let the browser set it with the boundary
		if (config.data instanceof FormData) {
			delete config.headers["Content-Type"];
		}

		// // Log the request for debugging
		// console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`,
		//   config.params ? `Params: ${JSON.stringify(config.params)}` : '');

		return config;
	},
	(error) => {
		console.error("Request Error:", error);
		return Promise.reject(error);
	}
);

// Enhanced error handling in response interceptor
apiClient.interceptors.response.use(
	(response: AxiosResponse) => {
		return response;
	},
	(error: AxiosError) => {
		if (error.response) {
			// The request was made and the server responded with a status code
			// that falls out of the range of 2xx
			console.error(
				"Response Error:",
				error.response.status,
				error.response.data
			);
			const { errors = [] }: any = error.response.data || {};
			const [message] = errors;
			// Background calls (such as notification registration) ask for no pop-up:
			// the merchant did nothing that could have failed.
			if (!(error.config as any)?.silentError) {
				toast.error(isTechnical(message) ? "Something went wrong. Please try again." : message || "Something went wrong!");
			}
		} else if (error.request) {
			// The request was made but no response was received
			console.error("No Response Error:", error.request);
		} else {
			// Something happened in setting up the request that triggered an Error
			console.error("Request Setup Error:", error.message);
			toast.error(error.message || "Something went wrong!");
		}

		return Promise.reject(error);
	}
);

// Internal error text from the server (database queries, Java exceptions) means
// nothing to a merchant and must not be shown as it is.
const isTechnical = (message: any): boolean =>
	typeof message === "string" &&
	/(\$java|query\s*\{|exception|non unique result|stack ?trace|\bat (org|java|com)\.|mongo|feign|nullpointer|sql)/i.test(message);

const apiService = {
	get: <T>(
		url: string,
		params?: Record<string, any>,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse<T>> => apiClient.get<T>(url, { ...config, params }),
	// Binary download (e.g. settlement invoice PDF) — reuses the auth interceptor.
	getBlob: (
		url: string,
		params?: Record<string, any>
	): Promise<AxiosResponse<Blob>> =>
		apiClient.get(url, { params, responseType: "blob" }),
	post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
		apiClient.post<T>(url, data, config),
	put: <T>(url: string, data?: any): Promise<AxiosResponse<T>> =>
		apiClient.put<T>(url, data),
	delete: <T>(url: string): Promise<AxiosResponse<T>> =>
		apiClient.delete<T>(url),
	patch: <T>(url: string, data?: any): Promise<AxiosResponse<T>> =>
		apiClient.patch<T>(url, data),
};

export default apiService;
